import { useState, useEffect, useRef } from "react";
import React from "react";
import Spinner from "react-bootstrap/Spinner";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../../components/atoms/button/Button";
import ReusableModal from "../../components/atoms/modal/ReusableModal";
import SimpleTable from "../../components/atoms/table/simpleTable/simpleTable";
import Add from "../../components/Assests/Icons/Add.png";
import Edit from "../../components/Assests/Icons/Edit.png";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import {
  getCustomerListData,
  addCustomerData,
  editCustomerData,
  getProductTypeData,
  getSpeciesData,
  getUnitsData,
  addSamplesToCustomer,
  saveRegisteredUserByAdmin,
  getRegisteredUserByEmail,
  getSalesRepData,
  getSalesRepresentativeNameByLocationId,
  getLocationsByCompany,
  approveUser,
  getProductTrades,
  getProductsByTradeId,
  getFeedTypeByTradeAndProduct,
  getSpeciesByTradeAndProductAndFeed,
  getProductConfiguration,
  getCompanyListData,
} from "./companyManagementApi";
import "./companyManagement.css";
import { toast, ToastContainer } from "react-toastify";
import DynamicForm from "../../components/atoms/dynamicForm";
import { getSampleFields, getUserFields } from "./fields/sampleFields";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { getCustomerFields } from "./fields/customerFields";
import { getJsonFromBrowserStorage } from "../../services/browserStorage";

const SwitchCell = ({
  row,
  field,
  apiKey,
  toastMsg,
  setCustomerList,
  fetchCustomers,
  disabled = false,
  tooltipText = "",
}) => {
  const resolvedTooltip = typeof tooltipText === "function" ? tooltipText(row) : tooltipText;
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{resolvedTooltip}</Tooltip>}
    >
      <label className="switch">
        <input
          type="checkbox"
          checked={!!row[field]}
          onChange={async (e) => {
            if (disabled) {
              let msg = "";
              if (apiKey === "showHistoricalData")
                msg = "Can't update Historical Data for new Customer.";
              else if (apiKey === "isLocationAdmin")
                msg = "Can't update Location Admin for new Customer.";
              else if (apiKey === "isActive")
                msg = "Can't update User Status for new Customer.";
              if (msg) toast.error(msg);
              e.preventDefault();
              e.stopPropagation();

              return;
            }
            const newValue = e.target.checked;
            if (apiKey === "showHistoricalData" || apiKey === "isLocationAdmin") {
              const nameParts = (row.customerName || "").split(" ");
              const firstName = nameParts[0] || "";
              const lastName =
                nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

              const payload = {
                role: row.salesRepId || "",
                firstName,
                lastName,
                email: row.email || "",
                location: row.location || "",
                customerId: row.customerId || "",
                showHistoricalData:
                  apiKey === "showHistoricalData"
                    ? newValue
                    : !!row["historicalData"],
                isLocationAdmin:
                  apiKey === "isLocationAdmin"
                    ? newValue
                    : !!row["locationAdmin"],
                isActive:
                  apiKey === "isActive" ? newValue : !!row["actionSlider"],
                isDeleted: false,
              };

              try {
                await editCustomerData(payload);
                toast.success(toastMsg);
                fetchCustomers();
              } catch (error) {
                toast.error(`Failed to update ${toastMsg.toLowerCase()}`);
              }
            } else {
              try {
                await approveUser(row.customerId, newValue);
                toast.success(toastMsg);
                fetchCustomers();
              } catch (error) {
                toast.error(`Failed to update ${toastMsg.toLowerCase()}`);
              }
            }
          }}
        />
        <span className={`slider ${row[field] ? "roundOn" : "roundOff"}`}></span>
      </label>
    </OverlayTrigger>
  );
};

const extractValue = (data, fieldNames) => {
  for (const fieldName of fieldNames) {
    if (
      data.hasOwnProperty(fieldName) &&
      data[fieldName] != null &&
      data[fieldName] !== ""
    ) {
      return data[fieldName];
    }
  }
  return "";
};

const getLocationFilteredSalesReps = (
  salesRepList = [],
  locationId,
  locationName = ""
) => {
  if (!Array.isArray(salesRepList) || salesRepList.length === 0) return [];
  if (!locationId && !locationName) return salesRepList;

  const normalizedLocationId = String(locationId || "");
  const normalizedLocationName = String(locationName || "").toLowerCase().trim();

  const hasLocationMappingInfo = salesRepList.some(
    (rep) =>
      rep?.locationId != null ||
      rep?.locationID != null ||
      Array.isArray(rep?.locationIds) ||
      Array.isArray(rep?.locations) ||
      typeof rep?.locationName === "string" ||
      typeof rep?.location === "string"
  );

  if (!hasLocationMappingInfo) return salesRepList;

  return salesRepList.filter((rep) => {
    const idCandidates = [rep?.locationId, rep?.locationID]
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value));

    if (normalizedLocationId && idCandidates.includes(normalizedLocationId)) {
      return true;
    }

    if (Array.isArray(rep?.locationIds)) {
      const ids = rep.locationIds.map((value) => String(value));
      if (normalizedLocationId && ids.includes(normalizedLocationId)) {
        return true;
      }
    }

    if (Array.isArray(rep?.locations)) {
      const matchedLocation = rep.locations.some((loc) => {
        if (loc == null) return false;
        if (typeof loc === "number" || typeof loc === "string") {
          return normalizedLocationId && String(loc) === normalizedLocationId;
        }

        const locId = loc.locationId ?? loc.id ?? loc.locationID;
        const locName = String(loc.name ?? loc.locationName ?? "")
          .toLowerCase()
          .trim();

        return (
          (normalizedLocationId && locId != null && String(locId) === normalizedLocationId) ||
          (normalizedLocationName && locName === normalizedLocationName)
        );
      });

      if (matchedLocation) return true;
    }

    const nameCandidates = [rep?.locationName, rep?.location]
      .filter((value) => typeof value === "string")
      .flatMap((value) =>
        String(value)
          .split(",")
          .map((part) => part.toLowerCase().trim())
      )
      .filter(Boolean);

    return !!normalizedLocationName && nameCandidates.includes(normalizedLocationName);
  });
};

const normalizeSalesRepList = (apiData) => {
  const rawList = Array.isArray(apiData)
    ? apiData
    : Array.isArray(apiData?.results)
      ? apiData.results
      : Array.isArray(apiData?.data)
        ? apiData.data
        : apiData && typeof apiData === "object"
          ? [apiData]
          : [];

  return rawList
    .map((rep) => {
      if (rep == null) return null;
      if (typeof rep === "string") {
        return {
          userID: "",
          fullName: rep,
        };
      }

      const userID =
        rep.userID ??
        rep.userId ??
        rep.id ??
        rep.fieldStaffId ??
        rep.salesRepresentativeId ??
        "";

      const fullName =
        rep.fullName ??
        rep.salesRepresentativeName ??
        rep.salesRepName ??
        rep.fieldStaffName ??
        rep.name ??
        "";

      if (!fullName) return null;

      return {
        ...rep,
        userID,
        fullName,
      };
    })
    .filter(Boolean);
};

const resolveSalesRepNameFromApiResponse = (apiData) => {
  if (typeof apiData === "string") return apiData;

  if (Array.isArray(apiData) && apiData.length > 0) {
    const first = apiData[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      return (
        extractValue(first, [
          "fullName",
          "salesRepresentativeName",
          "salesRepName",
          "fieldStaffName",
          "name",
        ]) || ""
      );
    }
  }

  if (apiData && typeof apiData === "object") {
    const directName = extractValue(apiData, [
      "fullName",
      "salesRepresentativeName",
      "salesRepName",
      "fieldStaffName",
      "name",
      "data",
      "result",
      "value",
    ]);

    if (typeof directName === "string" && directName) return directName;

    if (Array.isArray(apiData.data) && apiData.data.length > 0) {
      return resolveSalesRepNameFromApiResponse(apiData.data);
    }

    if (Array.isArray(apiData.results) && apiData.results.length > 0) {
      return resolveSalesRepNameFromApiResponse(apiData.results);
    }
  }

  return "";
};

const CustomerManagement = ({
  companyList = [],
  phibroRepOptions = [],
  showCustomerModal,
  setShowCustomerModal,
  search,
  setSearch,
  statusFilter = "Active",
  isAnalyst,
  openAddSample,
  onAddSampleOpened,
}) => {
  const [customerList, setCustomerList] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [editingUserData, setEditingUserData] = useState(null);
  const navigate = useNavigate();
  const [selectedCompanyLocations, setSelectedCompanyLocations] = useState([]);

  const defaultCustomerForm = {
    companyName: "",
    companyId: 0,
    location: "",
    locationId: 0,
    address: "",
    country: "",
    state: "",
    city: "",
    zipCode: "",
    phone: "",
    fax: "",
    fieldStaffId: "",

    firstName: "",
    lastName: "",
    email: "",
    registrationDate: "",
    customerId: 0,
    masterCompanyId: 0,
    masterLocationId: 0,
    masterSalesRep: "",
    masterSalesRepName: "",
  };

  const [customerForm, setCustomerForm] = useState(defaultCustomerForm);
  const [companyLocations, setCompanyLocations] = useState([]);
  const [masterCompanyLocations, setMasterCompanyLocations] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loadingAllCompanies, setLoadingAllCompanies] = useState(false);
  const [salesRepList, setSalesRepList] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchDebounced, setSearchDebounced] = useState(search || "");
  const [switchState, setSwitchState] = useState({
    historicalData: false,
    locationAdmin: false,
    actionSlider: false,
  });
  const [showAddSampleModal, setShowAddSampleModal] = useState(false);

  const [samples, setSamples] = useState([
    {
      id: 0,
      expanded: true,
      productTradeName: "",
      productType: "",
      feedType: "",
      species: "",
      unit: "",
      cfrMinLevel: "",
      cfrMaxLevel: "",
      loqValue: "",
      expectedLevel: "",
      phibroLots: "",
      customerSampleId: "",
      referenceNo: "",
      manufactureDate: "",
      remarks: "",
    },
  ]);

  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const sampleCounterRef = useRef(0);
  const [userDetails, setUserDetails] = useState({
    companyName: "",
    locationName: "",
    salesRep: "",
  });

  const [productTypes, setProductTypes] = useState([]);
  const [speciesList, setSpeciesList] = useState([]);
  const [unitsList, setUnitsList] = useState([]);

  const [availableProductTrades, setAvailableProductTrades] = useState([]);
  const [availableProductTypes, setAvailableProductTypes] = useState({});
  const [availableFeedTypes, setAvailableFeedTypes] = useState({});
  const [availableSpecies, setAvailableSpecies] = useState({});
  const [availableUnits, setAvailableUnits] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [productTradeOptions, setProductTradeOptions] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const sampleFormRefs = useRef([]);
  const dynamicFormRef = useRef();
  const customerSelectRef = useRef(null);
  const [sampleAllCompanies, setSampleAllCompanies] = useState([]);
  const [sampleCustomers, setSampleCustomers] = useState([]);
  const [allCachedCustomers, setAllCachedCustomers] = useState([]);
  const [addSampleLoading, setAddSampleLoading] = useState(false);

  // Auto-open Add Samples modal when triggered from side navigation
  useEffect(() => {
    if (!openAddSample) return;

    let cancelled = false;

    const loadAndOpen = async () => {
      setAddSampleLoading(true);

      const pageSize = 500;

      // Helper: fetch first page, then remaining pages in parallel
      const fetchAllPages = async (apiFn) => {
        const firstRes = await apiFn({ PageNumber: 1, PageSize: pageSize });
        if (!firstRes) return [];
        const firstPage = Array.isArray(firstRes.results) ? firstRes.results : [];
        const total = Number(firstRes.totalRecords) || firstPage.length;
        if (firstPage.length >= total || firstPage.length === 0) return firstPage;

        const totalPages = Math.ceil(total / pageSize);
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            apiFn({ PageNumber: i + 2, PageSize: pageSize })
          )
        );
        return remaining.reduce(
          (acc, res) => acc.concat(Array.isArray(res?.results) ? res.results : []),
          firstPage
        );
      };

      try {
        // Fetch companies, customers, sales reps, and dropdown data all in parallel
        const [companies, customers, salesRepData, tradesResponse, unitsResponse] = await Promise.all([
          fetchAllPages(getCompanyListData).catch((err) => {
            console.error("Failed to load companies for Add Sample:", err);
            return [];
          }),
          fetchAllPages(getCustomerListData).catch((err) => {
            console.error("Failed to load customers for Add Sample:", err);
            return [];
          }),
          getSalesRepData().catch((err) => {
            console.error("Failed to load sales reps for Add Sample:", err);
            return [];
          }),
          getProductTrades().catch((err) => {
            console.error("Failed to load product trades for Add Sample:", err);
            return [];
          }),
          getUnitsData().catch((err) => {
            console.error("Failed to load units for Add Sample:", err);
            return [];
          }),
        ]);

        if (cancelled) return;
        setSampleAllCompanies(companies);
        setAllCachedCustomers(customers);
        setSalesRepList(normalizeSalesRepList(salesRepData));
        setProductTradeOptions(tradesResponse || []);
        setAvailableUnits(unitsResponse || []);
      } catch (error) {
        console.error("Failed to load data for Add Sample:", error);
        if (cancelled) return;
      }

      // Set default userDetails with empty company (user will select from dropdown)
      setUserDetails({
        companyName: "",
        locationName: "",
        salesRep: "",
        companyId: 0,
        locationId: 0,
        customerId: 0,
        sampleRequstedBy:
          getJsonFromBrowserStorage("userData")?.email || "default@example.com",
        salesRepId: 0,
      });

      setShowAddSampleModal(true);
      setAddSampleLoading(false);
      onAddSampleOpened?.();
    };

    loadAndOpen();

    return () => { cancelled = true; };
  }, [openAddSample]);

  const [expandedCompanyIds, setExpandedCompanyIds] = useState([]);
  const [expandedLocationIds, setExpandedLocationIds] = useState([]);
  const [expandAll, setExpandAll] = useState(false);
  // Expand/Collapse All handler for locations
  const handleExpand = () => {
    if (!expandAll) {
      setExpandedLocationIds(groupedLocations.map((loc) => loc.key));
      setExpandAll(true);
    } else {
      setExpandedLocationIds([]);
      setExpandAll(false);
    }
  };
  const [locationCustomers, setLocationCustomers] = useState({});

  useEffect(() => {
    const minLength = 1;
    const delay = 1000;
    const handler = setTimeout(() => {
      if (!search || search.length === 0 || search.length >= minLength) {
        setSearchDebounced(search || "");
        setCurrentPage(1);
      }
    }, delay);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchCustomers(undefined, currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, searchDebounced, statusFilter]);

  useEffect(() => {
    const initializeDependentDropdowns = async () => {
      try {
        const [productTradesRes, unitsRes] = await Promise.all([
          getProductTrades(),
          getUnitsData(),
        ]);

        setAvailableProductTrades(productTradesRes || []);
        setAvailableUnits(unitsRes || []);
      } catch (error) {
        console.error("Failed to initialize dependent dropdowns:", error);
      }
    };

    initializeDependentDropdowns();
  }, []);

  useEffect(() => {
    if (showAddSampleModal && !isInitialized) {
      const loadDropdownData = async () => {
        try {
          const [trades, products, species, units] = await Promise.all([
            productTradeOptions.length > 0 ? Promise.resolve(productTradeOptions) : getProductTrades(),
            getProductTypeData(),
            getSpeciesData(),
            availableUnits.length > 0 ? Promise.resolve(availableUnits) : getUnitsData(),
          ]);

          setProductTradeOptions(trades || []);
          setProductTypes(Array.isArray(products) ? products : []);
          setSpeciesList(Array.isArray(species) ? species : []);
          setAvailableUnits(units || []);
          setUnitsList(Array.isArray(units) ? units : []);
        } catch (error) {
          console.error("Error loading dropdown data:", error);
          toast.error("Failed to load dropdown data");
        }
      };

      loadDropdownData();
      setIsInitialized(true);
    }
  }, [showAddSampleModal, isInitialized]);

  useEffect(() => {
    const loadLocationsForSample = async () => {
      if (showAddSampleModal && userDetails.companyId) {
        try {
          const locations = await getLocationsByCompany(userDetails.companyId);
          setCompanyLocations(Array.isArray(locations) ? locations : []);
        } catch (error) {
          console.error("Error fetching locations for sample modal:", error);
          setCompanyLocations([]);
        }
      }
    };

    loadLocationsForSample();
  }, [showAddSampleModal, userDetails.companyId]);

  useEffect(() => {
    const loadSalesRepsForSample = async () => {
      if (showAddSampleModal && salesRepList.length === 0) {
        try {
          const salesRepData = await getSalesRepData();
          setSalesRepList(normalizeSalesRepList(salesRepData));
        } catch (error) {
          console.error("Error fetching sales reps for sample modal:", error);
          setSalesRepList([]);
        }
      }
    };

    loadSalesRepsForSample();
  }, [showAddSampleModal]);

  const loadProductTypes = async (productTradeId, sampleId) => {
    if (!productTradeId) return;

    try {
      const productTypesData = await getProductsByTradeId(
        parseInt(productTradeId)
      );
      setAvailableProductTypes((prev) => ({
        ...prev,
        [sampleId]: productTypesData,
      }));
    } catch (error) {
      console.error("Failed to load product types:", error);
      toast.error("Failed to load product types");
    }
  };

  const loadFeedTypes = async (productTradeId, productTypeId, sampleId) => {
    if (!productTradeId || !productTypeId) return;

    try {
      const feedTypesData = await getFeedTypeByTradeAndProduct(
        parseInt(productTradeId),
        parseInt(productTypeId)
      );
      setAvailableFeedTypes((prev) => ({
        ...prev,
        [sampleId]: feedTypesData,
      }));
    } catch (error) {
      console.error("Failed to load feed types:", error);
      toast.error("Failed to load feed types");
    }
  };

  const loadSpecies = async (
    productTradeId,
    productTypeId,
    feedTypeId,
    sampleId
  ) => {
    if (!productTradeId || !productTypeId || !feedTypeId) return;

    try {
      const speciesData = await getSpeciesByTradeAndProductAndFeed(
        parseInt(productTradeId),
        parseInt(productTypeId),
        parseInt(feedTypeId)
      );

      const formattedSpecies = (speciesData || []).map((species) => ({
        speciesId: species.speciesId,
        speciesName: species.speciesName,
        masterSpeciesId: species.masterSpeciesId,
      }));

      setAvailableSpecies((prev) => ({
        ...prev,
        [sampleId]: formattedSpecies,
      }));
    } catch (error) {
      console.error("Failed to load species:", error);
      toast.error("Failed to load species");
    }
  };

  const loadProductConfiguration = async (
    productTradeId,
    productTypeId,
    feedTypeId,
    speciesId,
    sampleId,
    speciesListOverride
  ) => {
    if (!productTradeId || !productTypeId || !feedTypeId || !speciesId) {
      console.error(
        "Missing required parameters for GET_PRODUCT_CONFIGURATION API"
      );
      return;
    }

    const speciesArray = speciesListOverride || availableSpecies[sampleId];

    if (!speciesArray || !Array.isArray(speciesArray)) {
      console.error("Species array not found for sample ID:", sampleId);
      return;
    }

    const selectedSpecies = speciesArray.find(
      (s) => s && String(s.speciesId) === String(speciesId)
    );

    if (!selectedSpecies) {
      console.error("Selected species not found:", speciesId);
      return;
    }

    try {
      const configData = await getProductConfiguration(
        parseInt(productTradeId),
        parseInt(productTypeId),
        parseInt(feedTypeId),
        parseInt(selectedSpecies.masterSpeciesId)
      );

      const cfrMinLevel =
        configData.cfrMinLevel ??
        configData.minimumLevel ??
        configData.minLevel ??
        configData.minimumCFR ??
        "";

      const cfrMaxLevel =
        configData.cfrMaxLevel ??
        configData.maximumLevel ??
        configData.maxLevel ??
        configData.maximumCFR ??
        "";

      const loqValue =
        configData.loqValue ??
        configData.loq ??
        configData.limitOfQuantitation ??
        configData.LOQ ??
        "";

      setSamples((prev) =>
        prev.map((sample) =>
          sample.id === sampleId
            ? {
              ...sample,
              cfrMinLevel: cfrMinLevel?.toString() || "",
              cfrMaxLevel: cfrMaxLevel?.toString() || "",
              loqValue: loqValue?.toString() || "",
            }
            : sample
        )
      );

      if (cfrMinLevel || cfrMaxLevel || loqValue) {
      } else {
        console.warn("No configuration values found in response");
      }
    } catch (error) {
      console.error("Error in loadProductConfiguration:", error);

      setSamples((prev) =>
        prev.map((sample) =>
          sample.id === sampleId
            ? {
              ...sample,
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: "",
            }
            : sample
        )
      );

      toast.error("Failed to load configuration values");
    }
  };

  const fetchCustomers = async (searchtext, currentPage, rowsPerPage) => {
    setCustomerLoading(true);
    try {
      const effectiveSearch = typeof searchtext !== "undefined" ? searchtext : searchDebounced;
      const res = await getCustomerListData({
        PageNumber: currentPage,
        PageSize: rowsPerPage,
        ...(effectiveSearch && { FilterText: effectiveSearch }),
        Status: statusFilter,
      });
      const { totalRecords, pageNumber, pageSize, totalPages } = res;
      setPaginationData({ totalRecords, pageNumber, pageSize, totalPages });

      setCustomerList(
        Array.isArray(res.results)
          ? res.results.map((item) => ({
            companyName: item.companyName,
            location: item.locationName,
            customerName: item.customerName,
            email: item.email,
            historicalData: item.showHistoricalData,
            locationAdmin: item.isLocationAdmin,
            actionSlider: item.isActive,
            customerId: item.customerId,
            salesRepId: item.phiBroRepName,
            companyId: item.companyId,
            locationId: item.locationId,
            actualSalesRepId: item.salesRepId || item.phiBroRepId,
            isUpdated: item.isUpdated,
          }))
          : []
      );
      setTotalRecords(res.totalRecords);
    } catch {
      setCustomerList([]);
    }
    setCustomerLoading(false);
  };

  const fetchCustomersForLocation = async (locationId, locationName) => {
    setLocationCustomers((prev) => ({
      ...prev,
      [locationId]: { data: prev[locationId]?.data || [], loading: true },
    }));
    try {
      const res = await getCustomerListData({
        PageNumber: 1,
        PageSize: 500,
        ...(locationName && { FilterText: locationName }),
      });
      const customers = Array.isArray(res.results)
        ? res.results
          .filter((item) => String(item.locationId) === String(locationId))
          .map((item) => ({
            companyName: item.companyName,
            location: item.locationName,
            customerName: item.customerName,
            email: item.email,
            historicalData: item.showHistoricalData,
            locationAdmin: item.isLocationAdmin,
            actionSlider: item.isActive,
            customerId: item.customerId,
            salesRepId: item.phiBroRepName,
            companyId: item.companyId,
            locationId: item.locationId,
            actualSalesRepId: item.salesRepId || item.phiBroRepId,
            isUpdated: item.isUpdated,
          }))
        : [];
      setLocationCustomers((prev) => ({
        ...prev,
        [locationId]: { data: customers, loading: false },
      }));
    } catch {
      setLocationCustomers((prev) => ({
        ...prev,
        [locationId]: { data: [], loading: false },
      }));
    }
  };

  const handleCompanyExpandToggle = (companyId) => {
    setExpandedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleLocationExpandToggle = (locationId, locationName) => {
    setExpandedLocationIds((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      } else {
        if (!locationCustomers[locationId]?.data?.length) {
          fetchCustomersForLocation(locationId, locationName);
        }
        return [...prev, locationId];
      }
    });
  };

  const handleCompanyChange = async (companyName) => {
    const companiesSource = allCompanies && allCompanies.length > 0 ? allCompanies : companyList;
    const selectedCompany = companiesSource.find((c) => c.name === companyName);
    if (selectedCompany && selectedCompany.companyId) {
      try {
        const locations = await getLocationsByCompany(
          selectedCompany.companyId
        );
        setCompanyLocations(Array.isArray(locations) ? locations : []);
      } catch (error) {
        console.error("Error fetching locations:", error);
        setCompanyLocations([]);
      }
    } else {
      setCompanyLocations([]);
    }
  };

  const handleMasterCompanyChange = async (companyId) => {
    const companiesSource = allCompanies && allCompanies.length > 0 ? allCompanies : companyList;
    const selectedCompany = companiesSource.find(
      (c) => c.companyId === parseInt(companyId)
    );
    if (selectedCompany && selectedCompany.companyId) {
      try {
        const locations = await getLocationsByCompany(
          selectedCompany.companyId
        );
        setMasterCompanyLocations(Array.isArray(locations) ? locations : []);
      } catch (error) {
        console.error("Error fetching master company locations:", error);
        setMasterCompanyLocations([]);
      }
    } else {
      setMasterCompanyLocations([]);
    }
  };

  const handleProductTradeChange = async (productTradeId, sampleId) => {
    if (!productTradeId) {
      console.log("No product trade ID provided");
      return;
    }

    try {
      setSamples((prev) =>
        prev.map((sample) =>
          sample.id === sampleId
            ? {
              ...sample,
              productTradeName: productTradeId,
              productType: "",
              feedType: "",
              species: "",
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: "",
            }
            : sample
        )
      );

      setAvailableProductTypes((prev) => ({ ...prev, [sampleId]: [] }));
      setAvailableFeedTypes((prev) => ({ ...prev, [sampleId]: [] }));
      setAvailableSpecies((prev) => ({ ...prev, [sampleId]: [] }));

      const productTypes = await getProductsByTradeId(parseInt(productTradeId));

      if (!productTypes || productTypes.length === 0) {
        toast.warning("No product types available for the selected trade");
        return;
      }

      setAvailableProductTypes((prev) => ({
        ...prev,
        [sampleId]: productTypes.map((type) => ({
          productTypeId: type.productTypeId,
          name: type.name,
        })),
      }));
    } catch (error) {
      console.error("Error in handleProductTradeChange:", error);
      toast.error("Failed to load product types");
    }
  };

  const handleSampleChange = async (sampleId, field, value) => {
    setSamples((prev) =>
      prev.map((sample) =>
        sample.id === sampleId ? { ...sample, [field]: value } : sample
      )
    );

    try {
      // Build an up-to-date snapshot so recursive / chained calls never
      // read stale state from the `samples` closure.
      const baseSample = samples.find((s) => s.id === sampleId) || {};
      const sample = { ...baseSample, [field]: value };

      switch (field) {
        case "productType":
          if (value && sample.productTradeName) {
            setAvailableFeedTypes((prev) => ({ ...prev, [sampleId]: [] }));
            setAvailableSpecies((prev) => ({ ...prev, [sampleId]: [] }));

            const feedTypes = await getFeedTypeByTradeAndProduct(
              parseInt(sample.productTradeName),
              parseInt(value)
            );

            if (feedTypes && feedTypes.length > 0) {
              setAvailableFeedTypes((prev) => ({
                ...prev,
                [sampleId]: feedTypes,
              }));

              // Auto-select feed type with defaultSelection flag
              const defaultFeed = feedTypes.find((f) => f.defaultSelection);
              if (defaultFeed) {
                const defaultFeedId = String(defaultFeed.feedTypeId);
                // Update feedType in state
                setSamples((prev) =>
                  prev.map((s) =>
                    s.id === sampleId ? { ...s, feedType: defaultFeedId } : s
                  )
                );

                // Load species directly using known current values
                const species = await getSpeciesByTradeAndProductAndFeed(
                  parseInt(sample.productTradeName),
                  parseInt(value),
                  parseInt(defaultFeedId)
                );

                if (species && species.length > 0) {
                  setAvailableSpecies((prev) => ({
                    ...prev,
                    [sampleId]: species,
                  }));

                  // Auto-select species with defaultSelection flag
                  const defaultSpecies = species.find((s) => s.defaultSelection);
                  if (defaultSpecies) {
                    setSamples((prev) =>
                      prev.map((s) =>
                        s.id === sampleId
                          ? { ...s, species: String(defaultSpecies.speciesId) }
                          : s
                      )
                    );
                    await loadProductConfiguration(
                      parseInt(sample.productTradeName),
                      parseInt(value),
                      parseInt(defaultFeedId),
                      parseInt(defaultSpecies.speciesId),
                      sampleId,
                      species
                    );
                  }
                }
              }
            } else {
              toast.warning("No feed types available for the selected product");
            }
          }
          break;

        case "feedType":
          if (value && sample.productTradeName && sample.productType) {
            // clear species selection for this sample when feed type changes
            setSamples((prev) =>
              prev.map((s) => (s.id === sampleId ? { ...s, species: "" } : s))
            );
            setAvailableSpecies((prev) => ({ ...prev, [sampleId]: [] }));

            const species = await getSpeciesByTradeAndProductAndFeed(
              parseInt(sample.productTradeName),
              parseInt(sample.productType),
              parseInt(value)
            );

            if (species && species.length > 0) {
              setAvailableSpecies((prev) => ({
                ...prev,
                [sampleId]: species,
              }));

              // Auto-select species with defaultSelection flag
              const defaultSpecies = species.find((s) => s.defaultSelection);
              if (defaultSpecies) {
                setSamples((prev) =>
                  prev.map((s) =>
                    s.id === sampleId
                      ? { ...s, species: String(defaultSpecies.speciesId) }
                      : s
                  )
                );
                await loadProductConfiguration(
                  parseInt(sample.productTradeName),
                  parseInt(sample.productType),
                  parseInt(value),
                  parseInt(defaultSpecies.speciesId),
                  sampleId,
                  species
                );
              }
            } else {
              toast.warning("No species available for the selected feed type");
            }
          }
          break;

        case "species":
          if (
            value &&
            sample.productTradeName &&
            sample.productType &&
            sample.feedType
          ) {
            await loadProductConfiguration(
              parseInt(sample.productTradeName),
              parseInt(sample.productType),
              parseInt(sample.feedType),
              parseInt(value),
              sampleId
            );
          }
          break;
      }
    } catch (error) {
      console.error("Error in handleSampleChange:", error);
      toast.error("Failed to update dependent fields");
    }
  };

  const handleEditClick = async (row) => {
    try {
      let userDataResponse = null;
      try {
        userDataResponse = await getRegisteredUserByEmail(row.email);
      } catch (apiError) {
        console.error("Error fetching registered user by email:", apiError);
        userDataResponse = null;
      }

      const userData =
        userDataResponse && typeof userDataResponse === "object"
          ? userDataResponse.data && typeof userDataResponse.data === "object"
            ? userDataResponse.data
            : userDataResponse
          : {};

      setEditingUserData(userData);
      setEditMode("customer");

      const companiesSource = allCompanies && allCompanies.length > 0 ? allCompanies : companyList;
      const companyObj = companiesSource.find(
        (c) => c.name === userData.companyName || c.name === row.companyName
      );

      let locationObj = null;
      let companyLocationsData = [];

      if (companyObj && companyObj.companyId) {
        try {
          companyLocationsData = await getLocationsByCompany(
            companyObj.companyId
          );
          setCompanyLocations(
            Array.isArray(companyLocationsData) ? companyLocationsData : []
          );

          locationObj = companyLocationsData.find(
            (loc) =>
              loc.name === userData.locationName || loc.name === row.location
          );
        } catch (error) {
          console.error("Error fetching locations:", error);
          setCompanyLocations([]);
        }
      }

      // Find master company by ID first, then by name.
      // Fallback: the API stores masterCompanyId as companyId in the response.
      let masterCompanyObj = userData.masterCompanyId
        ? companiesSource.find((c) => c.companyId === userData.masterCompanyId)
        : null;

      if (!masterCompanyObj && userData.masterCompanyName) {
        masterCompanyObj = companiesSource.find(
          (c) => c.name === userData.masterCompanyName
        );
      }

      if (!masterCompanyObj && userData.companyId) {
        masterCompanyObj = companiesSource.find(
          (c) => c.companyId === userData.companyId
        );
      }

      if (!masterCompanyObj && userData.companyName) {
        masterCompanyObj = companiesSource.find(
          (c) => c.name === userData.companyName
        );
      }

      let masterLocationObj = null;
      let masterCompanyLocationsData = [];

      if (masterCompanyObj && masterCompanyObj.companyId) {
        try {
          masterCompanyLocationsData = await getLocationsByCompany(
            masterCompanyObj.companyId
          );
          setMasterCompanyLocations(
            Array.isArray(masterCompanyLocationsData)
              ? masterCompanyLocationsData
              : []
          );

          // Find master location by ID first, then by name
          masterLocationObj = userData.masterLocationId
            ? masterCompanyLocationsData.find(
              (loc) => loc.locationId === userData.masterLocationId
            )
            : null;

          if (!masterLocationObj && userData.masterLocation) {
            masterLocationObj = masterCompanyLocationsData.find(
              (loc) => loc.name === userData.masterLocation
            );
          }
        } catch (error) {
          console.error("Error fetching master company locations:", error);
          setMasterCompanyLocations([]);
        }
      }

      setSelectedCompanyLocations(
        Array.isArray(masterCompanyLocationsData) ? masterCompanyLocationsData : []
      );

      const registrationDateValue = userData.registrationDate
        ? new Date(userData.registrationDate).toLocaleString().split(",")[0]
        : "";

      const formData = {
        companyName: userData.companyName || row.companyName || "",
        companyId:
          userData.companyId || (companyObj ? companyObj.companyId : 0),
        location: userData.locationName || row.location || "",
        locationId:
          userData.locationId || (locationObj ? locationObj.locationId : 0),
        address: userData.address || "",
        country: userData.country || "",
        state: userData.state || "",
        city: userData.city || "",
        zipCode: userData.zipCode || "",
        phone: userData.phone || "",
        fax: userData.fax || "",
        phibroRep: userData.salesRep || row.salesRepId || "",
        salesRepId: userData.salesRepId || "",
        customerId: row.customerId || 0,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        email: userData.email || "",
        registrationDate: registrationDateValue,
        customerId: userData.customerId || row.customerId || 0,
        masterCompanyId: masterCompanyObj ? masterCompanyObj.companyId : (userData.masterCompanyId || userData.companyId || 0),
        masterLocationId: masterLocationObj ? masterLocationObj.locationId : (userData.masterLocationId || userData.locationId || 0),
        masterSalesRep: userData.masterSalesRepId || "",
        masterSalesRepName: userData.masterSalesRep || "",
      };

      setCustomerForm(formData);
      setShowCustomerModal(true);

      if (!userDataResponse) {
        toast.warning("Loaded basic customer data. Full user details were unavailable.");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Failed to load user data. Please try again.");
    }
  };
  useEffect(() => {
    let active = true;
    const companiesSource = allCompanies && allCompanies.length > 0 ? allCompanies : companyList;
    const found = companiesSource.find((c) => c.companyId == customerForm.masterCompanyId);
    const locations = found?.locations || [];
    if (locations && locations.length > 0) {
      setSelectedCompanyLocations(locations);
      return;
    }

    // If not found in the currently loaded lists, try fetching locations by company id
    const fetchLocationsFallback = async () => {
      if (!customerForm.masterCompanyId) {
        setSelectedCompanyLocations([]);
        return;
      }
      try {
        const res = await getLocationsByCompany(customerForm.masterCompanyId);
        if (active) setSelectedCompanyLocations(Array.isArray(res) ? res : []);
      } catch (error) {
        if (active) setSelectedCompanyLocations([]);
      }
    };

    if (locations.length === 0) {
      fetchLocationsFallback();
    }

    return () => {
      active = false;
    };
  }, [companyList, allCompanies, customerForm.masterCompanyId]);

  useEffect(() => {
    if (!showCustomerModal) return;
    if (customerForm.masterCompanyId) return;
    if (!customerForm.companyName) return;

    const companiesSource =
      allCompanies && allCompanies.length > 0 ? allCompanies : companyList;
    const matchingCompany = companiesSource.find(
      (c) => c.name === customerForm.companyName
    );

    if (!matchingCompany?.companyId) return;

    setCustomerForm((prev) => {
      if (prev.masterCompanyId) return prev;
      return {
        ...prev,
        masterCompanyId: matchingCompany.companyId,
      };
    });
  }, [
    showCustomerModal,
    customerForm.companyName,
    customerForm.masterCompanyId,
    allCompanies,
    companyList,
  ]);

  useEffect(() => {
    if (!showCustomerModal) return;
    if (customerForm.masterLocationId) return;
    // Only auto-restore on initial load — if address fields were cleared by the
    // user clearing the dropdown, do not re-fill masterLocationId.
    if (!customerForm.locationId) return;
    if (!customerForm.location) return;
    if (!Array.isArray(selectedCompanyLocations) || selectedCompanyLocations.length === 0) return;

    const matchingLocation = customerForm.locationId
      ? selectedCompanyLocations.find(
        (loc) => String(loc.locationId) === String(customerForm.locationId)
      )
      : selectedCompanyLocations.find(
        (loc) =>
          String(loc.name || loc.locationName || "") ===
          String(customerForm.location || "")
      );

    if (!matchingLocation?.locationId) return;

    setCustomerForm((prev) => {
      if (prev.masterLocationId) return prev;
      return {
        ...prev,
        masterLocationId: matchingLocation.locationId,
      };
    });
  }, [
    showCustomerModal,
    customerForm.masterLocationId,
    customerForm.locationId,
    customerForm.location,
    selectedCompanyLocations,
  ]);

  useEffect(() => {
    if (!showCustomerModal) return;
    if (!customerForm.masterSalesRep) return;

    const repObj = salesRepList.find(
      (rep) => String(rep.userID) === String(customerForm.masterSalesRep)
    );

    const resolvedName = repObj?.fullName || customerForm.masterSalesRepName || "";
    if (resolvedName === (customerForm.masterSalesRepName || "")) return;

    setCustomerForm((prev) => ({
      ...prev,
      masterSalesRepName: resolvedName,
    }));
  }, [
    showCustomerModal,
    customerForm.masterSalesRep,
    customerForm.masterSalesRepName,
    salesRepList,
  ]);

  useEffect(() => {
    if (!showCustomerModal) return;

    let active = true;

    const syncMasterSalesRepByLocation = async () => {
      const selectedLocationId = customerForm.masterLocationId;

      if (!selectedLocationId) {
        if (active) {
          setSalesRepList([]);
          setCustomerForm((prev) => ({
            ...prev,
            masterSalesRep: "",
            masterSalesRepName: "",
          }));
        }

        return;
      }

      try {
        const repsByLocation = await getSalesRepresentativeNameByLocationId(
          selectedLocationId
        );
        if (!active) return;

        const normalizedReps = normalizeSalesRepList(repsByLocation);
        setSalesRepList(normalizedReps);

        const selectedMasterLocation = (selectedCompanyLocations || []).find(
          (loc) => String(loc.locationId) === String(selectedLocationId)
        );

        const locationSpecificReps = getLocationFilteredSalesReps(
          normalizedReps,
          selectedLocationId,
          selectedMasterLocation?.name || selectedMasterLocation?.locationName || ""
        );
        const fallbackRepName = resolveSalesRepNameFromApiResponse(repsByLocation);

        setCustomerForm((prev) => {
          const currentRep = locationSpecificReps.find(
            (rep) => String(rep.userID) === String(prev.masterSalesRep)
          );
          const resolvedRep = currentRep || locationSpecificReps[0] || null;
          const nextMasterSalesRepName =
            resolvedRep?.fullName || fallbackRepName || "";
          const nextMasterSalesRep = resolvedRep
            ? resolvedRep.userID
            : prev.masterSalesRepName === nextMasterSalesRepName
              ? prev.masterSalesRep
              : "";

          if (
            String(prev.masterSalesRep || "") === String(nextMasterSalesRep || "") &&
            (prev.masterSalesRepName || "") === nextMasterSalesRepName
          ) {
            return prev;
          }

          return {
            ...prev,
            masterSalesRep: nextMasterSalesRep,
            masterSalesRepName: nextMasterSalesRepName,
          };
        });
      } catch (error) {
        if (!active) return;
        setSalesRepList([]);
        setCustomerForm((prev) => ({
          ...prev,
          masterSalesRep: "",
          masterSalesRepName: "",
        }));
      }
    };

    syncMasterSalesRepByLocation();

    return () => {
      active = false;
    };
  }, [
    showCustomerModal,
    customerForm.masterLocationId,
    selectedCompanyLocations,
  ]);

  useEffect(() => {
    if (!showCustomerModal) return;

    const selectedLocationId = customerForm.masterLocationId;

    if (!selectedLocationId) {
      setCustomerForm((prev) => ({
        ...prev,
        location: "",
        locationId: 0,
        address: "",
        country: "",
        state: "",
        city: "",
        zipCode: "",
        phone: "",
        fax: "",
      }));
      return;
    }

    const loc = (selectedCompanyLocations || []).find(
      (l) => String(l.locationId) === String(selectedLocationId)
    );

    if (!loc) {
      // Location not found in the current list. If the list is populated it means
      // the masterLocationId is stale (e.g. company was changed) — clear everything.
      if (Array.isArray(selectedCompanyLocations) && selectedCompanyLocations.length > 0) {
        setCustomerForm((prev) => {
          if (String(prev.masterLocationId) !== String(selectedLocationId)) return prev;
          return {
            ...prev,
            masterLocationId: "",
            location: "",
            locationId: 0,
            address: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
            phone: "",
            fax: "",
          };
        });
      }
      return;
    }

    setCustomerForm((prev) => {
      if (String(prev.masterLocationId) !== String(selectedLocationId)) return prev;

      const next = {
        ...prev,
        location: loc.name || loc.locationName || prev.location || "",
        locationId: Number(loc.locationId || selectedLocationId || 0),
        address: loc.address || "",
        country: loc.country || "",
        state: loc.state || "",
        city: loc.city || "",
        zipCode: loc.zipCode || loc.zip || "",
        phone: loc.phone || "",
        fax: loc.fax || "",
      };

      if (
        prev.location === next.location &&
        String(prev.locationId || "") === String(next.locationId || "") &&
        prev.address === next.address &&
        prev.country === next.country &&
        prev.state === next.state &&
        prev.city === next.city &&
        prev.zipCode === next.zipCode &&
        prev.phone === next.phone &&
        prev.fax === next.fax
      ) {
        return prev;
      }

      return next;
    });
  }, [
    showCustomerModal,
    customerForm.masterLocationId,
    selectedCompanyLocations,
  ]);

  const customerColumns = [
    { header: "Customer Name", accessor: "customerName" },
    { header: "Email", accessor: "email" },
    {
      header: "Historical Data",
      accessor: "historicalData",
      disableSort: true,
      cell: (row) => (
        <SwitchCell
          row={row}
          field="historicalData"
          apiKey="showHistoricalData"
          toastMsg="Historical Data status updated."
          setCustomerList={setCustomerList}
          fetchCustomers={fetchCustomers}
          disabled={!row.isUpdated}
          tooltipText={(r) => r.historicalData ? "Disable historical data" : "Enable historical data"}
        />
      ),
    },
    {
      header: "Location Admin",
      accessor: "isLocationAdmin",
      disableSort: true,
      cell: (row) => (
        <SwitchCell
          row={row}
          field="locationAdmin"
          apiKey="isLocationAdmin"
          toastMsg="Location Admin status updated."
          setCustomerList={setCustomerList}
          fetchCustomers={fetchCustomers}
          disabled={!row.isUpdated}
          tooltipText={(r) => r.locationAdmin ? "Remove location admin" : "Make location admin"}
        />
      ),
    },
    {
      header: "User Status",
      accessor: "isUserStatus",
      disableSort: true,
      cell: (row) => (
        <SwitchCell
          row={row}
          field="actionSlider"
          apiKey="isActive"
          toastMsg="Customer status updated."
          setCustomerList={setCustomerList}
          fetchCustomers={fetchCustomers}
          disabled={!row.isUpdated}
          tooltipText={(r) => r.actionSlider ? "Deactivate user" : "Activate user"}
        />
      ),
    },
    {
      header: "Action",
      accessor: "action",
      disableSort: true,
      cell: (row) => (
        <div className="d-flex align-items-center" style={{ gap: 20 }}>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-edit-customer-${row.customerId}`}>
                Edit customer
              </Tooltip>
            }
          >
            <Button
              size="sm"
              variant="edit-active"
              style={{
                padding: 0,
                background: "transparent",
                border: "none",
                marginRight: 20,
              }}
              onClick={() => handleEditClick(row)}
            >
              <img src={Edit} alt="Edit" width={24} height={24} />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-add-samples-${row.customerId}`}>
                Add Samples
              </Tooltip>
            }
          >
            <Button
              size="sm"
              variant="outlined"
              disabled={!row.actionSlider}
              style={{
                padding: 0,
                background: "transparent",
                border: "none",
                opacity: !row.actionSlider ? 0.5 : 1,
                cursor: !row.actionSlider ? "not-allowed" : "pointer",
              }}
              onClick={async () => {
                if (!row.actionSlider) return;

                const newUserDetails = {
                  companyName: row.companyName,
                  locationName: row.location,
                  salesRep: row.customerName || "",
                  companyId: row.companyId || 0,
                  locationId: row.locationId || 0,
                  customerId: row.customerId || 0,
                  sampleRequstedBy:
                    getJsonFromBrowserStorage("userData")?.email ||
                    "default@example.com",
                  salesRepId: row.actualSalesRepId || 0,
                };

                setUserDetails(newUserDetails);

                // Fetch locations, sales reps, product trades, and units in parallel
                const promises = [];

                if (row.companyId) {
                  promises.push(
                    getLocationsByCompany(row.companyId)
                      .then((locations) => setCompanyLocations(Array.isArray(locations) ? locations : []))
                      .catch((error) => { console.error("Error loading locations:", error); setCompanyLocations([]); })
                  );
                }

                promises.push(
                  getSalesRepData()
                    .then((salesReps) => setSalesRepList(normalizeSalesRepList(salesReps)))
                    .catch((error) => { console.error("Error loading sales reps:", error); setSalesRepList([]); }),
                  getProductTrades()
                    .then((trades) => setProductTradeOptions(trades || []))
                    .catch((error) => { console.error("Error loading product trades:", error); }),
                  getUnitsData()
                    .then((units) => setAvailableUnits(units || []))
                    .catch((error) => { console.error("Error loading units:", error); })
                );

                await Promise.all(promises);

                setShowAddSampleModal(true);
              }}
            >
              <img src={Add} alt="Add" width={24} height={24} />
            </Button>
          </OverlayTrigger>
        </div>
      ),
    },
  ];

  const locationTabColumns = [
    { header: "Company", accessor: "companyName" },
    { header: "Location", accessor: "location" },
    {
      header: (
        <Button onClick={handleExpand} style={{ minWidth: 110 }}>
          {expandAll ? "Collapse All" : "Expand All"}
        </Button>
      ),
      accessor: "expand",
      disableSort: true,
    },
  ];

  // Group customerList by locationId to build outer grid rows
  const groupedLocations = (() => {
    const map = new Map();
    customerList.forEach((cust) => {
      const key = `${cust.companyId}_${cust.locationId}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          companyName: cust.companyName,
          location: cust.location,
          companyId: cust.companyId,
          locationId: cust.locationId,
          customers: [],
        });
      }
      map.get(key).customers.push(cust);
    });
    return Array.from(map.values());
  })();

  const defaultSample = (id = 1) => ({
    id,
    expanded: true,
    productTradeName: "",
    productType: "",
    feedType: "",
    species: "",
    unit: "",
    cfrMinLevel: "",
    cfrMaxLevel: "",
    loqValue: "",
    expectedLevel: "",
    phibroLots: "",
    customerSampleId: "",
    referenceNo: "",
    manufactureDate: "",
    remarks: "",
  });

  const handleAddSample = () => {
    const nextId = ++sampleCounterRef.current;
    setSamples((prev) => {
      const lastSample = prev[prev.length - 1];

      if (!lastSample) {
        return [...prev, defaultSample(nextId)];
      }

      // Carry forward previous selections for faster entry of similar samples.
      const copiedSample = {
        ...defaultSample(nextId),
        productTradeName: lastSample.productTradeName || "",
        productType: lastSample.productType || "",
        feedType: lastSample.feedType || "",
        species: lastSample.species || "",
        unit: lastSample.unit || "",
        cfrMinLevel: lastSample.cfrMinLevel || "",
        cfrMaxLevel: lastSample.cfrMaxLevel || "",
        loqValue: lastSample.loqValue || "",
        expectedLevel: lastSample.expectedLevel || "",
        phibroLots: lastSample.phibroLots || "",
        remarks: lastSample.remarks || "",
      };

      setAvailableProductTypes((prevTypes) => ({
        ...prevTypes,
        [nextId]: [...(prevTypes[lastSample.id] || [])],
      }));
      setAvailableFeedTypes((prevFeeds) => ({
        ...prevFeeds,
        [nextId]: [...(prevFeeds[lastSample.id] || [])],
      }));
      setAvailableSpecies((prevSpecies) => ({
        ...prevSpecies,
        [nextId]: [...(prevSpecies[lastSample.id] || [])],
      }));

      return [...prev, copiedSample];
    });
  };

  const handleRemoveSample = (id) =>
    setSamples((prev) => prev.filter((s) => s.id !== id));

  const handleExpandToggle = (id) =>
    setSamples((prev) =>
      prev.map((s) => (s.id === id ? { ...s, expanded: !s.expanded } : s))
    );

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    let allValid = true;

    if (sampleFormRefs.current.length) {
      for (let i = 0; i < sampleFormRefs.current.length; i++) {
        const ref = sampleFormRefs.current[i];
        if (ref && typeof ref.validate === "function") {
          if (!ref.validate()) {
            allValid = false;
          }
        }
      }
    }

    if (!allValid) {
      toast.error("Please fill all required fields in the samples.");
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    const validateSampleData = (sample, index) => {
      const requiredFields = [
        { field: "productTradeName", label: "Product Trade" },
        { field: "productType", label: "Product Type" },
        { field: "feedType", label: "Feed Type" },
        { field: "species", label: "Species" },
        { field: "expectedLevel", label: "Expected Level" },
        { field: "phibroLots", label: "Phibro Lot No." },
        { field: "customerSampleId", label: "Customer Sample ID" },
      ];

      for (const { field, label } of requiredFields) {
        if (!sample[field] || sample[field].toString().trim() === "") {
          toast.error(`Please fill ${label} for sample ${index + 1}`);
          return false;
        }
      }
      return true;
    };

    const isValid = samples.every((sample, index) =>
      validateSampleData(sample, index)
    );
    if (!isValid) {
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (
      !userDetails.sampleRequstedBy ||
      userDetails.sampleRequstedBy.trim() === ""
    ) {
      toast.error(
        "Sample requested by email is required. Please check your login."
      );
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (!userDetails.salesRepId || userDetails.salesRepId === 0) {
      toast.error("Please select a Phibro Rep before submitting.");
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (sampleAllCompanies.length > 0 && (!userDetails.customerId || userDetails.customerId === 0)) {
      toast.error("Please select a Customer before submitting.");
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const companyId = userDetails.companyId || 0;
      const locationId = userDetails.locationId || 0;
      // Prefer the Customer dropdown's selected value; fall back to userDetails.customerId
      const dropdownValue = customerSelectRef.current ? Number(customerSelectRef.current.value) : 0;
      const customerId = dropdownValue || Number(userDetails.customerId) || 0;
      const sampleRequstedBy = userDetails.sampleRequstedBy.trim();
      const salesRepId = parseInt(userDetails.salesRepId) || 0;

      const samplesPayload = samples.map((sample, index) => {
        return {
          // If the sample came from the server it should carry sample.sampleId (or sample.sampleID).
          // For newly added UI rows we must send sampleId = 0 so backend creates new samples.
          sampleId: sample.sampleId
            ? Number(sample.sampleId)
            : sample.sampleID
              ? Number(sample.sampleID)
              : 0,
          productTradeNameId: parseInt(sample.productTradeName) || 0,
          productTypeId: parseInt(sample.productType) || 0,
          feedTypeId: parseInt(sample.feedType) || 0,
          speciesId: parseInt(sample.species) || 0,
          unitId: parseInt(sample.unit) || 0,
          locationId: locationId,
          companyId: companyId,
          salesRepId: salesRepId,

          cfrMinLevel: sample.cfrMinLevel ? sample.cfrMinLevel.toString() : "0",
          cfrMaxLevel: sample.cfrMaxLevel ? sample.cfrMaxLevel.toString() : "0",
          loqValue: sample.loqValue ? sample.loqValue.toString() : "",
          expectedLevel: sample.expectedLevel ? sample.expectedLevel : "",
          lotNumber: sample.phibroLots || "",
          customerSampleId: sample.customerSampleId || "",
          referenceNo: sample.referenceNo || "",
          manufactureDate: sample.manufactureDate || null,
          remarks: sample.remarks || "",
          barcodeNumber: sample.barcodeNumber || "",
          AssayResult: "",
        };
      });

      const payload = {
        companyId,
        locationId,
        phibroRep: salesRepId,
        customerId,
        sampleRequstedBy,
        samples: samplesPayload,
      };

      const result = await addSamplesToCustomer(payload);
      toast.success("Samples submitted successfully!");

      setTimeout(() => {
        console.log(isAnalyst, "<isAnalyst>")
        navigate(
          isAnalyst
            ? `/analyst-companyManagement/print-preview?groupId=${result.groupId}`
            : `/company-management/print-preview?groupId=${result.groupId}`,
          {
            state: { samples, userDetails, groupId: result.groupId },
          }
        );
      }, 3000);

      setShowAddSampleModal(false);
    } catch (error) {
      console.error("Submit error:", error);

      if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData.errors) {
          Object.keys(errorData.errors).forEach((field) => {
            const messages = errorData.errors[field];
            if (Array.isArray(messages)) {
              messages.forEach((message) =>
                toast.error(`${field}: ${message}`)
              );
            }
          });
        } else {
          toast.error(
            errorData.title ||
            "Validation error occurred. Please check your input."
          );
        }
      } else {
        toast.error("Error submitting samples. Please try again later.");
      }
    }
    submittingRef.current = false;
    setSubmitting(false);
  };

  const resetCustomerForm = () => {
    setCustomerForm(defaultCustomerForm);
    setEditMode(null);
    setCompanyLocations([]);
    setMasterCompanyLocations([]);
    setEditingUserData(null);
  };

  const handleAsyncAction = async (action, errorMessage) => {
    try {
      return await action();
    } catch (error) {
      console.error(errorMessage, error);
      toast.error(errorMessage);
      return null;
    }
  };

  // When Add/Edit Customer modal opens, fetch the full company list so the
  // Master Company dropdown can show all companies (the `companyList` prop is
  // paginated). Cache the result in `allCompanies` to avoid refetching.
  useEffect(() => {
    let active = true;
    const fetchAllCompanies = async () => {
      if (!showCustomerModal) return;
      if (allCompanies && allCompanies.length > 0) return; // already loaded

      setLoadingAllCompanies(true);
      try {
        const pageSize = 500;
        let page = 1;
        let accumulated = [];
        while (true) {
          const res = await getCompanyListData({ PageNumber: page, PageSize: pageSize });
          if (!res) break;
          const pageResults = Array.isArray(res.results) ? res.results : [];
          accumulated = accumulated.concat(pageResults);
          const total = Number(res.totalRecords) || accumulated.length;
          if (accumulated.length >= total || pageResults.length === 0) break;
          page += 1;
        }
        if (active) setAllCompanies(accumulated);
      } catch (error) {
        console.error("Failed to fetch full company list:", error);
      } finally {
        if (active) setLoadingAllCompanies(false);
      }
    };

    fetchAllCompanies();

    return () => {
      active = false;
    };
  }, [showCustomerModal, allCompanies]);

  return (
    <div className="company-management-container">
      {addSampleLoading && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(255,255,255,0.7)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 10,
        }}>
          <Spinner animation="border" />
          <span style={{ fontWeight: 500 }}>Loading Add Sample...</span>
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        containerId="customerManagementToast"
      />
      <div className="company-table-section">
        {customerLoading && !addSampleLoading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spinner animation="border" /> Loading...
          </div>
        ) : (
          <SimpleTable
            columns={locationTabColumns}
            data={groupedLocations}
            showPagination
            paginationData={paginationData}
            searchValue={search}
            fetchData={fetchCustomers}
            customRowRender={(loc) => (
              <React.Fragment key={loc.key}>
                <tr>
                  <td>{loc.companyName}</td>
                  <td>{loc.location}</td>
                  <td style={{ width: 60, textAlign: "center" }}>
                    <span
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        handleLocationExpandToggle(loc.key)
                      }
                    >
                      {expandedLocationIds.includes(loc.key) ? (
                        <FaChevronUp size={14} />
                      ) : (
                        <FaChevronDown size={14} />
                      )}
                    </span>
                  </td>
                </tr>
                {expandedLocationIds.includes(loc.key) && (
                  <tr>
                    <td colSpan={3}>
                      <div className="customer-table-wrapper">
                        {loc.customers.length > 0 ? (
                          <SimpleTable
                            columns={customerColumns}
                            data={loc.customers}
                            showPagination={false}
                          />
                        ) : (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "16px 0",
                              color: "#888",
                              fontSize: 14,
                            }}
                          >
                            No customers found for this location.
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )}
          />
        )}
      </div>
      <ReusableModal
        show={showCustomerModal}
        onHide={() => {
          setShowCustomerModal(false);
          resetCustomerForm();
        }}
        title={editMode === "customer" ? "Edit Customer" : "Add Customer"}
        dialogClassName="add-customer-modal"
        size="lg"
        buttons={[
          {
            text: "Cancel",
            variant: "outlined",
            onClick: () => {
              setShowCustomerModal(false);
              resetCustomerForm();
            },
          },
          {
            text: editMode === "customer" ? "Update" : "Add",
            variant: "active",
            onClick: async () => {
              if (dynamicFormRef.current.validate()) {
                if (editMode === "customer") {
                  const firstName = customerForm?.firstName;
                  const lastName = customerForm?.lastName;
                  const email = customerForm?.email;

                  const companiesSource = allCompanies && allCompanies.length > 0 ? allCompanies : companyList;
                  const selectedCompany = companiesSource.find(
                    (c) => c.name === customerForm.companyName
                  );
                  const selectedLocation = companyLocations.find(
                    (loc) => loc.name === customerForm.location
                  );
                  const selectedRep = salesRepList.find(
                    (rep) => rep.fullName === customerForm.phibroRep
                  );

                  const masterCompany = companiesSource.find(
                    (c) => c.companyId === customerForm.masterCompanyId
                  );
                  const masterLocation = selectedCompanyLocations.find(
                    (loc) => loc.locationId === customerForm.masterLocationId
                  );
                  const masterRep = salesRepList.find(
                    (rep) => rep.userID === customerForm.masterSalesRep
                  );

                  const userData = {
                    companyId: customerForm.masterCompanyId,
                    companyName: customerForm.companyName || "",
                    locationId: customerForm.masterLocationId,
                    fieldStaffId: customerForm.masterSalesRep,
                    locationName: customerForm.location || "",
                    address: customerForm.address || "",
                    country: customerForm.country || "",
                    state: customerForm.state || "",
                    city: customerForm.city || "",
                    zipCode: customerForm.zipCode || "",
                    phone: customerForm.phone || "",
                    fax: customerForm.fax || "",
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    email: email.trim(),
                    isActive: true,
                    customerId: customerForm.customerId,
                  };

                  try {
                    const response = await saveRegisteredUserByAdmin(userData);
                    if (response && response.success) {
                      toast.success("Customer updated successfully!");
                      setShowCustomerModal(false);
                      resetCustomerForm();
                      fetchCustomers();
                    } else {
                      console.error("API Response:", response);
                      toast.error(
                        response?.error ||
                        response?.message ||
                        "Customer update failed. Please try again."
                      );
                    }
                  } catch (error) {
                    console.error("Update Error:", error);
                    if (error.response) {
                      const errorMessage =
                        error.response.data?.message ||
                        error.response.data?.error ||
                        `Server Error: ${error.response.status}`;
                      toast.error(errorMessage);
                    } else if (error.request) {
                      toast.error(
                        "Network error. Please check your connection and try again."
                      );
                    } else {
                      toast.error(
                        "An unexpected error occurred. Please try again."
                      );
                    }
                  }
                } else {
                  const normalizedCompanyId = Number(customerForm.masterCompanyId || 0);
                  const normalizedLocationId = Number(customerForm.masterLocationId || 0);
                  const normalizedFieldStaffId = Number(customerForm.masterSalesRep || 0);

                  const payload = {
                    companyId: normalizedCompanyId,
                    locationId: normalizedLocationId,
                    fieldStaffId: normalizedFieldStaffId,
                    salesRepId: normalizedFieldStaffId,
                    companyName: customerForm.companyName,
                    location: customerForm.location,
                    locationName: customerForm.location,
                    salesRep: customerForm.masterSalesRepName || "",
                    firstName: customerForm.firstName,
                    lastName: customerForm.lastName,
                    email: customerForm.email,
                    address: customerForm.address,
                    country: customerForm.country,
                    state: customerForm.state,
                    city: customerForm.city,
                    zip: customerForm.zipCode,
                    zipCode: customerForm.zipCode,
                    phone: customerForm.phone,
                    fax: customerForm.fax,
                    customerId: customerForm.customerId || 0,
                    isActive: true,
                  };

                  try {
                    await addCustomerData(payload);
                    toast.success("Customer(s) added successfully!");
                    console.log("Add Customer Success:", payload);
                    setShowCustomerModal(false);
                    resetCustomerForm();
                    fetchCustomers();
                  } catch (error) {
                    const errorData = error?.response?.data;
                    const status = error?.response?.status;
                    // Backend saves successfully but returns 500 with empty body — treat as success
                    if (status === 500 && (errorData === "" || errorData === null || errorData === undefined)) {
                      toast.success("Customer(s) added successfully!");
                      setShowCustomerModal(false);
                      resetCustomerForm();
                      fetchCustomers();
                    } else if (errorData?.errors && typeof errorData.errors === "object") {
                      Object.keys(errorData.errors).forEach((field) => {
                        const messages = errorData.errors[field];
                        if (Array.isArray(messages)) {
                          messages.forEach((message) =>
                            toast.error(`${field}: ${message}`)
                          );
                        }
                      });
                    } else {
                      console.log("Add Customer Error:", error);
                      console.log("Error Response Data:", errorData);
                      toast.error(
                        errorData?.title ||
                        errorData?.message ||
                        (typeof errorData === "string" && errorData ? errorData : null) ||
                        "Error adding customer(s). Please check your input or try again later."
                      );
                    }
                  }
                }
              }
            },
          },
        ]}
      >
        <DynamicForm
          key={`${editMode}-${customerForm.customerId}`} // Force remount when editing different customer
          onChange={(newForm) => {
            setCustomerForm((prev) => {
              const merged = { ...prev, ...newForm };

              if (
                Object.prototype.hasOwnProperty.call(newForm, "masterCompanyId") &&
                newForm.masterCompanyId !== prev.masterCompanyId
              ) {
                const companiesSource =
                  allCompanies && allCompanies.length > 0
                    ? allCompanies
                    : companyList;
                const selectedMasterCompany = companiesSource.find(
                  (c) => String(c.companyId) === String(newForm.masterCompanyId)
                );

                merged.companyName = selectedMasterCompany?.name || "";

                // Company changed — clear location and all location-derived fields
                merged.masterLocationId = "";
                merged.location = "";
                merged.locationId = 0;
                merged.address = "";
                merged.country = "";
                merged.state = "";
                merged.city = "";
                merged.zipCode = "";
                merged.phone = "";
                merged.fax = "";
                merged.masterSalesRep = "";
                merged.masterSalesRepName = "";
              }

              if (
                Object.prototype.hasOwnProperty.call(newForm, "masterLocationId") &&
                newForm.masterLocationId !== prev.masterLocationId
              ) {
                const selectedMasterLocation = (selectedCompanyLocations || []).find(
                  (loc) => String(loc.locationId) === String(newForm.masterLocationId)
                );

                if (!newForm.masterLocationId) {
                  // Location cleared — wipe all location-derived fields immediately.
                  // Use prev values (not newForm) so DynamicForm's stale internal state
                  // doesn't override these explicit clears.
                  merged.location = "";
                  merged.locationId = 0;
                  merged.address = "";
                  merged.country = "";
                  merged.state = "";
                  merged.city = "";
                  merged.zipCode = "";
                  merged.phone = "";
                  merged.fax = "";
                  // Also zero in newForm so downstream spreads can't re-introduce old values
                  merged.masterLocationId = "";
                } else {
                  merged.location =
                    selectedMasterLocation?.name ||
                    selectedMasterLocation?.locationName ||
                    "";
                }

                // Clear current master rep until location-scoped reps are fetched.
                merged.masterSalesRep = "";
                merged.masterSalesRepName = "";
              }

              return merged;
            });
          }}
          initialData={customerForm}
          fields={(() => {
            const baseFields = getCustomerFields(
              allCompanies && allCompanies.length > 0 ? allCompanies : companyList,
              selectedCompanyLocations,
              salesRepList,
              customerForm,
              editMode
            );
            if (loadingAllCompanies) {
              // replace masterCompanyId options with a disabled Loading... option
              return baseFields.map((f) =>
                f.name === "masterCompanyId"
                  ? { ...f, disabled: true, options: [{ label: "Loading...", value: "" }] }
                  : f
              );
            }
            return baseFields;
          })()}
          editMode={editMode}
          ref={dynamicFormRef}
        />
      </ReusableModal>

      <ReusableModal
        show={showAddSampleModal}
        onHide={() => {
          setShowAddSampleModal(false);
          setAvailableProductTypes({});
          setAvailableFeedTypes({});
          setAvailableSpecies({});

          setCompanyLocations([]);
          setSalesRepList([]);
          setSampleAllCompanies([]);
          setSampleCustomers([]);
          setAllCachedCustomers([]);

          setSamples([
            {
              id: 0,
              expanded: true,
              productTradeName: "",
              productType: "",
              feedType: "",
              species: "",
              unit: "",
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: "",
              expectedLevel: "",
              phibroLots: "",
              customerSampleId: "",
              referenceNo: "",
              manufactureDate: "",
              remarks: "",
            },
          ]);
        }}
        title="Add Samples"
        size="xl"
        centered={false}
        dialogClassName="add-sample-modal"
        buttons={[]}
      >
        <div className="company-info">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Company Name</label>
              {sampleAllCompanies.length > 0 ? (
                <select
                  className="form-select"
                  value={userDetails.companyId || ""}
                  onChange={async (e) => {
                    const selectedCompanyId = parseInt(e.target.value);
                    const selectedCompany = sampleAllCompanies.find(
                      (c) => c.companyId === selectedCompanyId
                    );
                    if (selectedCompany) {
                      setUserDetails((prev) => ({
                        ...prev,
                        companyId: selectedCompany.companyId,
                        companyName: selectedCompany.name,
                        locationId: 0,
                        locationName: "",
                        salesRepId: 0,
                        salesRep: "",
                        customerId: 0,
                      }));
                      setSampleCustomers([]);
                      try {
                        const locations = await getLocationsByCompany(selectedCompany.companyId);
                        setCompanyLocations(Array.isArray(locations) ? locations : []);
                      } catch (error) {
                        console.error("Error loading locations:", error);
                        setCompanyLocations([]);
                      }
                    } else {
                      setUserDetails((prev) => ({
                        ...prev,
                        companyId: 0,
                        companyName: "",
                        locationId: 0,
                        locationName: "",
                        salesRepId: 0,
                        salesRep: "",
                        customerId: 0,
                      }));
                      setCompanyLocations([]);
                      setSampleCustomers([]);
                    }
                  }}
                >
                  <option value="">Select Company</option>
                  {sampleAllCompanies.map((company) => (
                    <option key={company.companyId} value={company.companyId}>
                      {company.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={userDetails.companyName || ""}
                  disabled
                  style={{ backgroundColor: "#f8f9fa" }}
                />
              )}
            </div>
            <div className="col-md-4">
              <label className="form-label">Location</label>
              <select
                className="form-select"
                value={userDetails.locationId || ""}
                onChange={(e) => {
                  const selectedLocationId = parseInt(e.target.value);
                  const selectedLocation = companyLocations.find(
                    (loc) => loc.locationId === selectedLocationId
                  );
                  if (selectedLocation) {
                    setUserDetails((prev) => ({
                      ...prev,
                      locationId: selectedLocation.locationId,
                      locationName: selectedLocation.name,
                      customerId: 0,
                    }));
                    // Filter customers from cache by locationName (IDs differ between Location and Users tables)
                    const filtered = allCachedCustomers.filter(
                      (c) => c.locationName === selectedLocation.name && c.isActive === true && !c.isDeleted
                    );
                    setSampleCustomers(filtered);
                    if (filtered.length === 1) {
                      setUserDetails((prev) => ({
                        ...prev,
                        customerId: Number(filtered[0].customerId),
                      }));
                    }
                  } else {
                    setUserDetails((prev) => ({
                      ...prev,
                      locationId: 0,
                      locationName: "",
                      customerId: 0,
                    }));
                    setSampleCustomers([]);
                  }
                }}
                style={{
                  backgroundColor:
                    companyLocations.length === 0 ? "#f8f9fa" : "white",
                }}
              >
                <option value="">Select Location</option>
                {[...companyLocations].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((location) => (
                  <option key={location.locationId} value={location.locationId}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
            {sampleAllCompanies.length > 0 && (
              <div className="col-md-4">
                <label className="form-label">Customer</label>
                <select
                  ref={customerSelectRef}
                  className="form-select"
                  value={userDetails.customerId || ""}
                  onChange={(e) => {
                    const selectedCustomerId = parseInt(e.target.value);
                    const selectedCustomer = sampleCustomers.find(
                      (c) => Number(c.customerId) === selectedCustomerId
                    );
                    if (selectedCustomer) {
                      setUserDetails((prev) => ({
                        ...prev,
                        customerId: Number(selectedCustomer.customerId),
                      }));
                    } else {
                      setUserDetails((prev) => ({
                        ...prev,
                        customerId: 0,
                      }));
                    }
                  }}
                  disabled={sampleCustomers.length === 0}
                  style={{
                    backgroundColor:
                      sampleCustomers.length === 0 ? "#f8f9fa" : "white",
                  }}
                  title="Select Customer"
                >
                  <option value="">Select Customer</option>
                  {sampleCustomers.map((customer) => (
                    <option key={customer.customerId} value={customer.customerId}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-4">
              <label className="form-label">
                Select Phibro Rep <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={userDetails.salesRepId || ""}
                onChange={(e) => {
                  const selectedSalesRepId = parseInt(e.target.value);
                  const selectedSalesRep = salesRepList.find(
                    (rep) => rep.userID === selectedSalesRepId
                  );
                  if (selectedSalesRep) {
                    setUserDetails((prev) => ({
                      ...prev,
                      salesRepId: selectedSalesRep.userID,
                      salesRep: selectedSalesRep.fullName,
                    }));
                  }
                }}
                style={{
                  backgroundColor:
                    salesRepList.length === 0 ? "#f8f9fa" : "white",
                }}
              >
                <option value="">Select Phibro Rep</option>
                {salesRepList.map((rep) => (
                  <option key={rep.userID} value={rep.userID}>
                    {rep.fullName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="customer-sample mt-1" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
          {samples.map((sample, idx) => (
            <div key={sample.id} className="sample-block mb-3">
              <div className="sample-header-bar">
                <span className="sample-title">
                  {idx + 1}. Customer Sample ID:{" "}
                  {sample.customerSampleId || `0${idx + 1}`}
                </span>
                <div className="sample-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleExpandToggle(sample.id)}
                    title={sample.expanded ? "Collapse sample" : "Expand sample"}
                  >
                    {sample.expanded ? "▲" : "▼"}
                  </button>
                  {samples.length > 1 && (
                    <>
                      <span className="divider"></span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleRemoveSample(sample.id)}
                        title="Remove sample"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </div>
              </div>

              {sample.expanded && (
                <div className="sample-content px-3 pb-3 pt-0">
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label">
                        Product Trade Name{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={sample.productTradeName || ""}
                        onChange={(e) =>
                          handleProductTradeChange(e.target.value, sample.id)
                        }
                        title="Select Product Trade Name"
                      >
                        <option value="">Select Product Trade</option>
                        {productTradeOptions.map((option) => (
                          <option
                            key={option.productTradeNameId}
                            value={option.productTradeNameId}
                          >
                            {option.productTradeName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Product Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={sample.productType || ""}
                        onChange={(e) => {
                          handleSampleChange(
                            sample.id,
                            "productType",
                            e.target.value
                          );
                        }}
                        disabled={!sample.productTradeName}
                        title="Select Product Type"
                      >
                        <option value="">Select Product Type</option>
                        {(availableProductTypes[sample.id] || []).map(
                          (type) => (
                            <option
                              key={type.productTypeId}
                              value={String(type.productTypeId)}
                            >
                              {type.name}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Feed Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={sample.feedType || ""}
                        onChange={(e) => {
                          handleSampleChange(
                            sample.id,
                            "feedType",
                            e.target.value
                          );
                        }}
                        disabled={!sample.productType}
                        title="Select Feed Type"
                      >
                        <option value="">Select Feed Type</option>
                        {(availableFeedTypes[sample.id] || []).map((feed) => (
                          <option
                            key={feed.feedTypeId}
                            value={String(feed.feedTypeId)}
                          >
                            {feed.feedTypeName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label">
                        Species <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={sample.species || ""}
                        onChange={(e) => {
                          handleSampleChange(
                            sample.id,
                            "species",
                            e.target.value
                          );
                        }}
                        disabled={!sample.feedType}
                        title="Select Species"
                      >
                        <option value="">Select Species</option>
                        {(availableSpecies[sample.id] || [])
                          .filter((s) => s && s.speciesId && s.speciesName)
                          .map((s) => (
                            <option key={s.speciesId} value={s.speciesId}>
                              {s.speciesName}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Unit <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={sample.unit || ""}
                        onChange={(e) =>
                          handleSampleChange(sample.id, "unit", e.target.value)
                        }
                        title="Select Unit"
                      >
                        <option value="">Select Unit</option>
                        {availableUnits.map((unit) => (
                          <option key={unit.unitId} value={String(unit.unitId)}>
                            {unit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <div className="field">
                        <label>CFR Min Level</label>
                        <input
                          value={sample.cfrMinLevel || ""}
                          readOnly
                          disabled
                          style={{ backgroundColor: "#f8f9fa" }}
                          title="CFR Min Level"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="field">
                      <label>CFR Max Level</label>
                      <input
                        value={sample.cfrMaxLevel || ""}
                        readOnly
                        disabled
                        style={{ backgroundColor: "#f8f9fa" }}
                        title="CFR Max Level"
                      />
                    </div>
                    <div className="col-md-4">
                      <div className="field">
                        <label>LOQ Value</label>
                        <input
                          value={sample.loqValue || ""}
                          readOnly
                          disabled
                          style={{ backgroundColor: "#f8f9fa" }}
                          title="LOQ Value"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Expected Level <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={sample.expectedLevel || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "expectedLevel",
                            e.target.value
                          )
                        }
                        placeholder="Enter Expected Level"
                        title="Expected Level"
                        placeholder="Enter Expected Level"
                        title="Enter expected level (non-numeric values like '<300' are treated as 0 for calculations)"
                      />
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label">
                        Phibro Lot No. <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={sample.phibroLots || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "phibroLots",
                            e.target.value
                          )
                        }
                        placeholder="Enter Phibro Lot No."
                        title="Phibro Lot No."
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Customer Sample ID{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={sample.customerSampleId || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "customerSampleId",
                            e.target.value
                          )
                        }
                        placeholder="Enter Customer Sample ID"
                        title="Customer Sample ID"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Reference No</label>
                      <input
                        type="text"
                        className="form-control"
                        value={sample.referenceNo || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "referenceNo",
                            e.target.value
                          )
                        }
                        placeholder="Enter Reference Number"
                        title="Reference No"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Manufacture Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={sample.manufactureDate || ""}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "manufactureDate",
                            e.target.value
                          )
                        }
                        placeholder="Select Manufacture Date"
                      />
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Remarks</label>
                      <textarea
                        className="form-control"
                        value={sample.remarks || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "remarks",
                            e.target.value
                          )
                        }
                        rows="4"
                        placeholder="Enter any additional remarks here..."
                        title="Remarks"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="sample-actions-footer" style={{ borderTop: "1px solid #dee2e6", paddingTop: "0.75rem", marginTop: "0.25rem" }}>
          <Button
            className="sample-action-btn"
            variant="outlined"
            onClick={handleAddSample}
            disabled={submitting}
          >
            Add Another Sample
          </Button>
          <Button
            className="sample-action-btn"
            variant="active"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                {" "}Submitting...
              </>
            ) : (
              "Submit Samples"
            )}
          </Button>
        </div>
      </ReusableModal>
    </div>
  );
};

export default CustomerManagement;